/**
 * Sub-account row — parity with ys142857 #/pages/user/subAccount cards.
 */
import { useTranslation } from 'react-i18next';
import { THEMED_SURFACE_COLORS as C } from '../ui/ThemedSurface';
import { formatWalletBalance } from '@/utils/wallet-display';

export type SubAccountListItem = {
  id: number;
  /** Legacy player-facing ID (ys142857 `number`). */
  player_number?: string | null;
  username: string;
  create_time?: string;
  level_code?: string;
  balances?: Record<string, string>;
  uo?: string;
  ep1?: string;
  rp?: string;
  sp1?: string;
  sp2?: string;
  sp3?: string;
  mp?: string;
  avatar_url?: string | null;
  static_summary?: {
    earnings_usd: string;
    exit_label: 'exited' | 'active';
    level_alias: string;
    star_count: number;
  };
};

function bal(sub: SubAccountListItem, key: keyof SubAccountListItem): string {
  if (sub.balances?.[key]) return sub.balances[key];
  const top = sub[key];
  return typeof top === 'string' ? top : '0';
}

function fmtHold(raw: string): string {
  const n = Number(formatWalletBalance(raw).replace(/,/g, ''));
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtOrbTop(raw: string): string {
  const n = Number(formatWalletBalance(raw).replace(/,/g, ''));
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtEarnings(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return '0.00$';
  return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}$`;
}

function fmtDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 19).replace('T', ' ');
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function MetricCell({
  value,
  label,
  valueColor = C.text,
}: {
  value: string;
  label: string;
  valueColor?: string;
}) {
  return (
    <div className="min-w-0 text-center">
      <div className="truncate text-xs font-semibold font-mono leading-tight" style={{ color: valueColor }}>
        {value}
      </div>
      <div className="mt-0.5 truncate text-[9px] leading-none" style={{ color: C.muted }}>
        {label}
      </div>
    </div>
  );
}

export function SubAccountListCard({
  sub,
  active,
  onSelect,
}: {
  sub: SubAccountListItem;
  active: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const summary = sub.static_summary;
  const stars = summary?.star_count ?? 1;
  const exitText =
    summary?.exit_label === 'exited'
      ? t('subAccounts.exited', '已出局')
      : t('subAccounts.notExited', '未出局');
  const levelAlias = summary?.level_alias || sub.level_code || '—';

  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full rounded-xl border px-3 py-3 text-left transition-all active:scale-[0.99]"
      style={{
        borderColor: active ? `${C.gold}88` : C.line,
        background: active ? C.chipBg : C.inputBg,
        boxShadow: active ? `0 0 12px ${C.gold}22` : undefined,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="size-12 shrink-0 overflow-hidden rounded-full border"
          style={{ borderColor: `${C.gold}55`, background: C.chipBg }}
        >
          {sub.avatar_url ? (
            <img src={sub.avatar_url} alt="" className="size-full object-cover" draggable={false} />
          ) : (
            <div className="flex size-full items-center justify-center text-sm font-bold" style={{ color: C.gold }}>
              {(sub.username?.[0] ?? '?').toUpperCase()}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 text-[11px] leading-snug" style={{ color: C.muted }}>
              <div>ID: {sub.player_number ?? sub.id}</div>
              <div className="truncate">
                {t('subAccounts.loginName', '登录名')}: {sub.username}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-base font-bold leading-none" style={{ color: C.gold }}>
                {fmtOrbTop(bal(sub, 'uo'))}
              </div>
              <div className="mt-0.5 text-[10px] font-semibold" style={{ color: C.muted }}>
                ORB
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-1 border-t pt-3" style={{ borderColor: `${C.gold}22` }}>
        <MetricCell value={fmtHold(bal(sub, 'ep1'))} label={t('subAccounts.holdEp1', '持有EP1')} />
        <MetricCell value={fmtHold(bal(sub, 'rp'))} label={t('subAccounts.holdRp', '持有RP')} />
        <MetricCell value={fmtHold(bal(sub, 'sp1'))} label={t('subAccounts.holdSp1', '持有SP1')} valueColor="#58A6FF" />
        <MetricCell value={fmtHold(bal(sub, 'sp3'))} label={t('subAccounts.holdSp3', '持有SP3')} valueColor="#f778ba" />
      </div>

      <div className="mt-2 grid grid-cols-4 gap-1">
        <MetricCell value={fmtHold(bal(sub, 'mp'))} label={t('subAccounts.holdMp', '持有MP')} />
        <MetricCell
          value={fmtEarnings(summary?.earnings_usd ?? '0')}
          label={t('subAccounts.earnings', '收益')}
          valueColor="#F74E5E"
        />
        <MetricCell value={exitText} label={t('subAccounts.exitStatus', '出局')} valueColor={C.text} />
        <MetricCell value={levelAlias} label={t('subAccounts.levelAlias', 'BA/VA')} valueColor={C.gold} />
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-0.5 text-sm leading-none" style={{ color: C.gold }}>
          {Array.from({ length: stars }).map((_, i) => (
            <span key={i}>★</span>
          ))}
        </div>
        <div className="text-[10px] font-mono" style={{ color: C.muted }}>
          {fmtDateTime(sub.create_time)}
        </div>
      </div>
    </button>
  );
}
